import { useEffect, useState } from "react";
import moment from "moment";
import { useTheme } from "styled-components";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { Alert, AutogrowTextarea, Button, ContentLoader, Icon, LoadingSpinner } from "@webtypen/react-components";
import { ApiHelper, ConfirmHelper } from "../helpers";

export const ShiftRequestStatus = ({ status, style, ...rest }) => {
    const theme = useTheme();

    return (
        <span
            style={{
                padding: "5px 10px",
                background: "#eee",
                borderRadius: 3,
                userSelect: "none",
                color:
                    status === "confirmed"
                        ? theme.color.success
                        : status === "declined"
                        ? theme.color.danger
                        : undefined,
                ...style,
            }}
            {...rest}
        >
            {status === "pending"
                ? "Offen"
                : status === "declined"
                ? "Abgelehnt"
                : status === "confirmed"
                ? "Angenommen"
                : status}
        </span>
    );
};

export const ApplyForShiftForm = ({ shiftId, onSubmitStart, onSubmitEnd }) => {
    const [initing, setIniting] = useState(true);
    const [init, setInit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [note, setNote] = useState("");

    const removeRequest = async () => {
        const status = await ConfirmHelper.confirm(
            "Möchtest du deine Bewerbung für diese Schicht wirklich zurückziehen?",
            { confirmAttributes: { color: "danger" } }
        );
        if (!status) {
            return;
        }

        if (onSubmitStart) {
            onSubmitStart();
        }

        setIsRemoving(true);
        ApiHelper.request("/api/employees-shifts/shift-request/remove", {
            _id: init._id,
        })
            .then((response) => {
                if (response.status === "success") {
                    ConfirmHelper.alert("Du hast deine Bewerbung erfolgreich zurückgezogen!", {
                        confirmAttributes: { color: "success" },
                    });
                } else {
                    ConfirmHelper.alert(
                        response.status === "error" && response.message && response.message.trim() !== ""
                            ? response.message
                            : "Es ist ein unerwarteter Fehler aufgetreten ...",
                        {
                            confirmAttributes: { color: "danger" },
                        }
                    );
                }

                if (onSubmitEnd) {
                    onSubmitEnd(response);
                }
                setIsRemoving(false);
            })
            .catch((e) => {
                console.error(e);
                ConfirmHelper.alert(
                    e && e.data && e.data.status === "error" && e.data.message && e.data.message.trim() !== ""
                        ? e.data.message
                        : "Es ist ein unerwarteter Fehler aufgetreten ...",
                    {
                        confirmAttributes: { color: "danger" },
                    }
                );

                if (onSubmitEnd) {
                    onSubmitEnd(e.data);
                }
                setIsRemoving(false);
            });
    };

    const submit = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (onSubmitStart) {
            onSubmitStart();
        }

        setIsLoading(true);
        ApiHelper.request("/api/employees-shifts/apply-open-shift", {
            _id: shiftId,
            note: note,
        })
            .then((response) => {
                if (response.status === "success") {
                    ConfirmHelper.alert("Du hast dich erfolgreich für diese Schicht beworben!", {
                        confirmAttributes: { color: "success" },
                    });
                } else {
                    ConfirmHelper.alert(
                        response.status === "error" && response.message && response.message.trim() !== ""
                            ? response.message
                            : "Es ist ein unerwarteter Fehler aufgetreten ...",
                        {
                            confirmAttributes: { color: "danger" },
                        }
                    );
                }

                if (onSubmitEnd) {
                    onSubmitEnd(response);
                }
                setIsLoading(false);
            })
            .catch((e) => {
                console.error(e);
                ConfirmHelper.alert(
                    e && e.data && e.data.status === "error" && e.data.message && e.data.message.trim() !== ""
                        ? e.data.message
                        : "Es ist ein unerwarteter Fehler aufgetreten ...",
                    {
                        confirmAttributes: { color: "danger" },
                    }
                );

                if (onSubmitEnd) {
                    onSubmitEnd(e.data);
                }
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (!shiftId) {
            setIniting(false);
            return;
        }

        setIniting(true);
        setInit(false);
        ApiHelper.request("/api/employees-shifts/shift-request/details", {
            byShift: shiftId,
        })
            .then((response) => {
                if (response.status === "success" && response.data && response.data._id) {
                    setInit(response.data);
                }
                setIniting(false);
            })
            .catch((e) => {
                setIniting(false);
            });
    }, [shiftId]);

    if (!shiftId) {
        return <Alert danger>Missing shift-id ...</Alert>;
    }

    if (initing) {
        return <ContentLoader />;
    }

    if (init) {
        return (
            <div>
                Du hast dich bereits für diese Schicht beworben:
                <div>{moment(init.created_at).calendar()}</div>
                <div className="mt-2">
                    <b>Status:</b>
                    {" " +
                        (init.status === "pending"
                            ? "Offen"
                            : init.status === "confirmed"
                            ? "Angenommen"
                            : init.status === "declined"
                            ? "Abgelehnt"
                            : init.status)}
                </div>
                <div>
                    <b>Notiz:</b>
                    {" " + (init.note && init.note.trim() !== "" ? init.note : "")}
                </div>
                {init.status === "pending" ? (
                    <Button className="mt-3" type="button" onClick={removeRequest} disabled={isRemoving} light sm>
                        {isRemoving ? <LoadingSpinner className="mr-2" /> : <Icon icon={faTimes} className="mr-2" />}
                        Bewerbung zurückziehen
                    </Button>
                ) : null}
            </div>
        );
    }

    return (
        <form onSubmit={submit}>
            Möchtest du dich wirklich für die ausgewählte Schicht eintragen?
            <AutogrowTextarea
                placeholder="Bemerkung"
                value={note ? note : ""}
                disabled={isLoading}
                style={{ minHeight: 36 }}
                onChange={(e) => setNote(e.target.value)}
            />
            <div className="text-right mt-2">
                <Button disabled={isLoading} success>
                    {isLoading ? <LoadingSpinner /> : "Eintragen"}
                </Button>
            </div>
        </form>
    );
};
